/* Le 28 schede di «I nostri servizi · Il metodo» (pagine Paese), dal
 * riferimento `uploads/servizi-rotazione-riferimento.html` con le istruzioni
 * `uploads/ISTRUZIONI-AGENTE-servizi-rotazione.md` (25/10/2026).
 *
 * I 14 servizi vengono dal catalogo (`AREE_PAGINA`): numero, area, domanda
 * dell'area, titolo, descrizione e frase sono quelli delle schede della
 * prima pagina e di /servizi, quindi se cambia il catalogo cambiano anche
 * qui. Le altre 14 (AI, Cibernetica, OSINT, Chi siamo) hanno i testi del
 * riferimento, che a sua volta li prende da /ai, /cibernetica, /osint e
 * /chi-siamo: se la redazione cambia quelle pagine, vanno aggiornate qui.
 *
 * Nessuna direttiva: lo leggono la pagina (server) e la scelta (client). */

import { AREE_PAGINA } from "@/lib/serviziPagina";

export type TipoScheda = "servizio" | "ai" | "cib" | "osint" | "chi";

export type SchedaRotazione = {
  tipo: TipoScheda;
  href: string;
  /** Per un servizio lo slug di `DisegnoServizio`, per le altre la chiave
   *  di `DISEGNI_ROTAZIONE`. */
  disegno: string;
  /** Il quadratino pieno: «08», «AI», «C1»… */
  sigla: string;
  /** La riga in mono accanto: «Servizio 08 di 14», «AI · Parte 1 di 3». */
  posto: string;
  categoria: string;
  titolo: string;
  /** La domanda o la citazione, Gelasio corsivo. */
  domanda: string;
  descrizione: string;
  invito: string;
};

const SERVIZI: SchedaRotazione[] = AREE_PAGINA.flatMap((a) =>
  a.servizi.map((s) => ({
    tipo: "servizio" as const,
    href: `/servizi/${s.slug}`,
    disegno: s.slug,
    sigla: s.numero,
    posto: `Servizio ${s.numero} di 14`,
    categoria: a.nome,
    titolo: s.titolo,
    domanda: `«${a.domanda}»`,
    descrizione: s.descrizione,
    invito: s.frase,
  }))
);

const ALTRE: SchedaRotazione[] = [
  {
    tipo: "ai",
    href: "/ai",
    disegno: "ai",
    sigla: "AI",
    posto: "Approfondimenti",
    categoria: "Percorso di adozione per PMI",
    titolo: "AI · Il sistema, prima dell’AI",
    domanda: "Progettare il sistema. Scegliere gli strumenti.",
    descrizione:
      "L’intelligenza artificiale è uno strumento: funziona solo quando appoggia su un disegno chiaro dell’impresa.",
    invito: "Parliamo della tua impresa",
  },
  {
    tipo: "ai",
    href: "/ai#percorso",
    disegno: "ai-percorso",
    sigla: "AI",
    posto: "AI · Parte 1 di 3",
    categoria: "AI · Percorso di adozione per PMI",
    titolo: "Come arriviamo all’adozione.",
    domanda: "Prima capiamo l’impresa, poi progettiamo il sistema.",
    descrizione:
      "Ascolto, valutazione, progettazione, selezione, adozione, manutenzione. Solo allora scegliamo e introduciamo gli strumenti.",
    invito: "Il percorso in sei tappe",
  },
  {
    tipo: "ai",
    href: "/ai#sistema",
    disegno: "ai-sistema",
    sigla: "AI",
    posto: "AI · Parte 2 di 3",
    categoria: "AI · Percorso di adozione per PMI",
    titolo: "Il sistema prima degli strumenti.",
    domanda: "La sequenza è logica, non cosmetica.",
    descrizione:
      "L’impresa, le persone e i processi alimentano il sistema informativo. Solo dopo si scelgono gli strumenti, AI compresa.",
    invito: "Vedi la mappa",
  },
  {
    tipo: "ai",
    href: "/ai#errori",
    disegno: "ai-errori",
    sigla: "AI",
    posto: "AI · Parte 3 di 3",
    categoria: "AI · Percorso di adozione per PMI",
    titolo: "Gli errori che spostano il lavoro.",
    domanda: "Comprare prima di progettare.",
    descrizione:
      "In 18 mesi la tecnologia cambia: senza un disegno, l’investimento invecchia. L’AI è potente ma fragile, va capita prima.",
    invito: "I quattro errori",
  },
  {
    tipo: "ai",
    href: "/ai#ai-cta-h",
    disegno: "ai-confronto",
    sigla: "AI",
    posto: "Approfondimenti",
    categoria: "AI · Un confronto riservato",
    titolo: "L’azienda è pronta per l’AI?",
    domanda: "A volte conviene rimandare.",
    descrizione:
      "Una conversazione riservata di 40 minuti, sotto NDA, per capirlo insieme. La proponiamo solo quando ha senso.",
    invito: "Apri la conversazione",
  },
  {
    tipo: "cib",
    href: "/cibernetica",
    disegno: "cibernetica",
    sigla: "CB",
    posto: "Sei capitoli · glossario",
    categoria: "Cibernetica · Il metodo dell’Osservatorio",
    titolo: "Il problema ce l’hai già, il numero arriva dopo.",
    domanda: "Un anello chiuso, non un muro.",
    descrizione:
      "Cibernetica non vuol dire cybersecurity: vuol dire sistemi che ricevono un’informazione di ritorno e la usano per correggersi.",
    invito: "Leggi il manuale",
  },
  {
    tipo: "cib",
    href: "/cibernetica#definizione",
    disegno: "cib-definizione",
    sigla: "C1",
    posto: "Capitolo 1 di 6",
    categoria: "Cibernetica · Il manuale",
    titolo: "Governo dei sistemi, non sicurezza informatica.",
    domanda: "«Se il sistema non si corregge da solo, non è cibernetico. È elettronica.»",
    descrizione: "Il caso: il preventivo che sbaglia sempre del quindici per cento.",
    invito: "Capitolo 1 · Definizione",
  },
  {
    tipo: "cib",
    href: "/cibernetica#anello-chiuso",
    disegno: "cib-anello-chiuso",
    sigla: "C2",
    posto: "Capitolo 2 di 6",
    categoria: "Cibernetica · Il manuale",
    titolo: "Il momento in cui il dato torna indietro.",
    domanda: "«Senza ritorno del dato a chi l’ha prodotto, è solo statistica.»",
    descrizione: "Il caso: il reclamo che si ferma al commerciale.",
    invito: "Capitolo 2 · Anello chiuso",
  },
  {
    tipo: "cib",
    href: "/cibernetica#errore-comune",
    disegno: "cib-errore-comune",
    sigla: "C3",
    posto: "Capitolo 3 di 6",
    categoria: "Cibernetica · Il manuale",
    titolo: "L’informatico non viene per primo.",
    domanda: "«Lo strumento che precede la domanda è arredamento, non controllo.»",
    descrizione: "Il caso: quarantamila euro di piattaforma, e un archivio.",
    invito: "Capitolo 3 · Errore comune",
  },
  {
    tipo: "cib",
    href: "/cibernetica#il-ritardo",
    disegno: "cib-il-ritardo",
    sigla: "C4",
    posto: "Capitolo 4 di 6",
    categoria: "Cibernetica · Il manuale",
    titolo: "Fra il fatto e il numero passa del tempo.",
    domanda: "«Ogni settimana di ritardo nella misura è una settimana regalata all’errore.»",
    descrizione: "Il caso: il margine di commessa letto a bilancio.",
    invito: "Capitolo 4 · Il ritardo",
  },
  {
    tipo: "cib",
    href: "/cibernetica#la-misura",
    disegno: "cib-la-misura",
    sigla: "C5",
    posto: "Capitolo 5 di 6",
    categoria: "Cibernetica · Il manuale",
    titolo: "Pochi numeri, quelli che cambiano una decisione.",
    domanda: "«Un numero che non cambia una decisione non è una misura: è un costo.»",
    descrizione: "Il caso: centoventi indicatori, tre decisioni.",
    invito: "Capitolo 5 · La misura",
  },
  {
    tipo: "cib",
    href: "/cibernetica#il-primo-mese",
    disegno: "cib-il-primo-mese",
    sigla: "C6",
    posto: "Capitolo 6 di 6",
    categoria: "Cibernetica · Il manuale",
    titolo: "Da dove si comincia, in concreto.",
    domanda: "«Il primo anello che si chiude non è il più importante: è quello che dimostra che si può.»",
    descrizione: "Il caso: quattro settimane, e alla fine un numero che si chiude ogni venerdì.",
    invito: "Capitolo 6 · Il primo mese",
  },
  {
    tipo: "osint",
    href: "/osint",
    disegno: "osint",
    sigla: "M",
    posto: "Il metodo",
    categoria: "Il metodo · Manuale aperto",
    titolo: "OSINT",
    domanda: "Dalla fonte all’informazione utile.",
    descrizione:
      "Un manuale aperto per riconoscere le fonti, distinguere i regimi del messaggio e costruire un prodotto di analisi.",
    invito: "Leggi il manuale OSINT",
  },
  {
    tipo: "chi",
    href: "/chi-siamo",
    disegno: "chi-siamo",
    sigla: "OI",
    posto: "Approfondimenti",
    categoria: "Osservatorio Italiano",
    titolo: "Chi siamo",
    domanda: "Una visione propria. I mezzi per costruirla.",
    descrizione:
      "Un lavoro sviluppato nel tempo tra informazione, tecnologia, economia e conoscenza dei territori.",
    invito: "Conosci l’Osservatorio",
  },
];

/** Le 28 schede, nell'ordine del riferimento: i 14 servizi, poi AI,
 *  Cibernetica, OSINT e Chi siamo. La scelta lavora sugli indici. */
export const SCHEDE_ROTAZIONE: SchedaRotazione[] = [...SERVIZI, ...ALTRE];

/** Le quattro schede prima dell'idratazione (e senza JavaScript): due
 *  servizi di aree diverse, una scheda AI, una di Cibernetica, come vuole la
 *  regola. Monitoraggio, Due diligence, «AI · Il sistema, prima dell’AI» e la
 *  presentazione del manuale di cibernetica. */
export const PRIMA_SCELTA_ROTAZIONE = [0, 7, 14, 19];

/** La regola del riferimento (`svScegli`), portata così com'è: due servizi
 *  di aree diverse, una scheda AI, una quarta che nel 60% dei casi è di
 *  Cibernetica e altrimenti OSINT o Chi siamo; l'ordine mescolato; e, se c'è
 *  un gruppo precedente, uno diverso da quello. Restituisce quattro indici di
 *  `SCHEDE_ROTAZIONE`. */
export function scegliSchede(prima: number[] | null): number[] {
  const indici = (t: (k: TipoScheda) => boolean) =>
    SCHEDE_ROTAZIONE.flatMap((s, i) => (t(s.tipo) ? [i] : []));
  const serv = indici((k) => k === "servizio");
  const ai = indici((k) => k === "ai");
  const cib = indici((k) => k === "cib");
  const altri = indici((k) => k === "osint" || k === "chi");
  const caso = (a: number[]) => a[Math.floor(Math.random() * a.length)]!;
  const firma = (a: number[]) => [...a].sort((x, y) => x - y).join();
  let scelti: number[];
  do {
    const a = caso(serv);
    let b: number;
    let giri = 0;
    do {
      b = caso(serv);
      giri++;
    } while (
      (b === a || SCHEDE_ROTAZIONE[b]!.categoria === SCHEDE_ROTAZIONE[a]!.categoria) &&
      giri < 50
    );
    scelti = [a, b, caso(ai), Math.random() < 0.6 ? caso(cib) : caso(altri)];
  } while (prima && firma(prima) === firma(scelti));
  for (let i = scelti.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [scelti[i], scelti[j]] = [scelti[j]!, scelti[i]!];
  }
  return scelti;
}

/* ── La colonna mista delle pagine dei nodi (/corridoi/[slug]) ──────────
   Richiesta del committente del 26/10/2026: nella colonna accanto agli
   Aggiornamenti, dopo l'editoriale, «le pubblicità, AI, chi siamo,
   cibernetica, card misti». Sono le stesse 28 schede, in un'altra forma
   (`SchedaColonna` in PaginaCorridoio) e con un'altra regola: sei schede,
   tre servizi di aree diverse alternati a tre approfondimenti (un'AI, una
   di Cibernetica, Chi siamo oppure OSINT). La colonna poi ne tiene quante
   ci stanno accanto agli Aggiornamenti (`useRiempiVuoto`). */

const indiceDi = (t: TipoScheda) => SCHEDE_ROTAZIONE.findIndex((s) => s.tipo === t);

/** Le sei prima dell'idratazione (e senza JavaScript): AI, Monitoraggio,
 *  Cibernetica, Due diligence, Chi siamo, il primo servizio della terza
 *  area. Fisse, perché il sorteggio nel render darebbe due colonne diverse
 *  fra server e browser. */
export const PRIMA_SCELTA_MISTA: number[] = [
  indiceDi("ai"),
  0,
  indiceDi("cib"),
  7,
  indiceDi("chi"),
  SCHEDE_ROTAZIONE.findIndex((s) => s.tipo === "servizio" && s.categoria === AREE_PAGINA[2]!.nome),
];

/** Il sorteggio della colonna mista: solo nel browser (usa `Math.random`). */
export function scegliMisti(): number[] {
  const mescola = <T,>(a: T[]) => {
    const b = [...a];
    for (let i = b.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [b[i], b[j]] = [b[j]!, b[i]!];
    }
    return b;
  };
  const indici = (t: (k: TipoScheda) => boolean) => SCHEDE_ROTAZIONE.flatMap((s, i) => (t(s.tipo) ? [i] : []));
  // Tre servizi, possibilmente di tre aree diverse.
  const servizi: number[] = [];
  for (const i of mescola(indici((k) => k === "servizio"))) {
    if (servizi.length === 3) break;
    if (!servizi.some((j) => SCHEDE_ROTAZIONE[j]!.categoria === SCHEDE_ROTAZIONE[i]!.categoria)) servizi.push(i);
  }
  const caso = (a: number[]) => a[Math.floor(Math.random() * a.length)]!;
  const altri = mescola([
    caso(indici((k) => k === "ai")),
    caso(indici((k) => k === "cib")),
    caso(indici((k) => k === "chi" || k === "osint")),
  ]);
  // Alternati, e a caso chi apre: un approfondimento o un servizio.
  const [a, b] = Math.random() < 0.5 ? [altri, servizi] : [servizi, altri];
  return a.flatMap((x, i) => (b[i] === undefined ? [x] : [x, b[i]!]));
}
